import { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Plus, 
  Search, 
  Filter,
  Radio,
  Wifi,
  WifiOff,
  Battery,
  Thermometer,
  Droplets,
  Zap,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  History,
  ShieldAlert,
  Loader2,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { sensorsService, companiesService, serversService } from '@/services';
import type { ApiSensor, CreateSensorRequest, UpdateSensorRequest } from '@/types/api';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useWebSocket } from '@/contexts/WebSocketContext';
import type { Sensor } from '@/types';
import { SensorDetailDialog } from '@/components/sensors/SensorDetailDialog';
import { SensorFormDialog } from '@/components/sensors/SensorFormDialog';
import { SensorHistoryDialog } from '@/components/sensors/SensorHistoryDialog';
import { SensorDeleteDialog } from '@/components/sensors/SensorDeleteDialog';
import { useToast } from '@/hooks/use-toast';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// Skeleton component
function SensorCardSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-xl" />
            <div>
              <Skeleton className="h-5 w-32 mb-1" />
              <Skeleton className="h-3 w-20" />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Skeleton className="h-5 w-16" />
          <Skeleton className="h-5 w-20" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
        </div>
      </CardContent>
    </Card>
  );
}

// Helper to convert API sensor to frontend Sensor type
// CAMBIO v0.2.0: Usar companyId en lugar de gardenId
// IMPORTANTE: El backend devuelve mqttServerId y lnsServerId directamente, no objetos anidados
function apiSensorToFrontend(apiSensor: ApiSensor): Sensor {
  const sensor: Sensor = {
    id: apiSensor.id,
    name: apiSensor.name,
    model: apiSensor.model as 'EM500-SMTC',
    deviceEUI: apiSensor.deviceEUI,
    companyId: apiSensor.companyId,
    coordinates: apiSensor.coordinates 
      ? { lat: apiSensor.coordinates.lat, lng: apiSensor.coordinates.lng }
      : undefined,
    communicationType: apiSensor.communicationType,
    mqttConfig: (apiSensor.mqttServerId || apiSensor.mqttTopic) ? {
      serverId: apiSensor.mqttServerId,
      topic: apiSensor.mqttTopic,
    } : undefined,
    lnsConfig: apiSensor.lnsServerId ? {
      serverId: apiSensor.lnsServerId,
    } : undefined,
    status: apiSensor.status,
    lastSeen: apiSensor.lastSeen ? new Date(apiSensor.lastSeen) : undefined,
    battery: apiSensor.battery,
    createdAt: new Date(apiSensor.createdAt),
    updatedAt: new Date(apiSensor.updatedAt),
  };
  
  // Añadir appKey si está presente (para ChirpStack)
  if (apiSensor.appKey) {
    (sensor as any).appKey = apiSensor.appKey;
  }
  
  return sensor;
}

export default function Sensors() {
  const [searchTerm, setSearchTerm] = useState('');
  const { canManageDevices, currentUser } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { lastSensorReading } = useWebSocket();

  // Dialog states
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSensor, setSelectedSensor] = useState<Sensor | null>(null);

  // Query para obtener sensores
  const { 
    data: sensorsData, 
    isLoading, 
    error,
    refetch 
  } = useQuery({
    queryKey: ['sensors', searchTerm],
    queryFn: async () => {
      const response = await sensorsService.getAll({ pageSize: 100 });
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al cargar sensores');
      }
      return response.data;
    },
  });

  // Obtener última lectura de cada sensor para mostrar en las tarjetas
  const apiSensors = sensorsData?.items || [];
  
  // Query para obtener última lectura de cada sensor
  // Usamos Promise.all para obtener todas las últimas lecturas en paralelo
  const { data: lastReadingsMap, isLoading: isLoadingLastReadings, error: lastReadingsError } = useQuery({
    queryKey: ['sensor-last-readings', apiSensors.length > 0 ? apiSensors.map(s => s.id).sort().join(',') : ''],
    queryFn: async () => {
      console.log('[Sensors] Fetching last readings for', apiSensors.length, 'sensors');
      const readingsMap = new Map<string, SensorReading>();
      
      // Obtener última lectura de cada sensor (últimas 24 horas)
      const today = new Date().toISOString().split('T')[0];
      const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      const results = await Promise.allSettled(
        apiSensors.map(async (apiSensor) => {
          try {
            const response = await sensorsService.getReadings(apiSensor.id, {
              start: yesterday,
              end: today,
            });
            if (response.success && response.data && response.data.length > 0) {
              // Ordenar por timestamp descendente y tomar la primera (más reciente)
              const sortedReadings = [...response.data]
                .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
              const latestReading = sortedReadings[0];
              readingsMap.set(apiSensor.id, {
                id: latestReading.id,
                sensorId: latestReading.sensorId,
                temperature: latestReading.temperature,
                humidity: latestReading.humidity,
                conductivity: latestReading.conductivity,
                timestamp: new Date(latestReading.timestamp),
              });
              console.log(`[Sensors] ✅ Last reading for ${apiSensor.name}:`, {
                temp: latestReading.temperature,
                hum: latestReading.humidity,
                cond: latestReading.conductivity,
              });
            } else {
              console.log(`[Sensors] ⚠️ No readings found for sensor ${apiSensor.name} (${apiSensor.id})`);
            }
          } catch (error) {
            console.error(`[Sensors] ❌ Error fetching readings for sensor ${apiSensor.name}:`, error);
          }
        })
      );
      
      console.log('[Sensors] Last readings map size:', readingsMap.size, 'out of', apiSensors.length);
      return readingsMap;
    },
    enabled: apiSensors.length > 0,
    // Refrescar cada 2 minutos para obtener nuevas lecturas
    refetchInterval: 120000,
    refetchIntervalInBackground: false,
    // Cache por 1 minuto para evitar demasiadas llamadas
    staleTime: 60000,
  });

  // Combinar sensores con sus últimas lecturas
  // También usar lecturas del WebSocket si están disponibles (más recientes)
  const sensors = useMemo(() => {
    const sensorsWithReadings = apiSensors.map(apiSensor => {
      const sensor = apiSensorToFrontend(apiSensor);
      
      // Priorizar lectura del WebSocket si es más reciente
      let lastReading: SensorReading | undefined = undefined;
      
      // Si hay una lectura del WebSocket para este sensor, usarla
      if (lastSensorReading && lastSensorReading.sensorId === sensor.id) {
        lastReading = {
          id: `ws-${Date.now()}`,
          sensorId: lastSensorReading.sensorId,
          temperature: lastSensorReading.temperature,
          humidity: lastSensorReading.humidity,
          conductivity: lastSensorReading.soilEC,
          timestamp: new Date(lastSensorReading.timestamp),
        };
        console.log(`[Sensors] ✅ Using WebSocket reading for ${sensor.name}:`, lastReading);
      } else {
        // Si no hay lectura del WebSocket, usar la de la query
        const queryReading = lastReadingsMap?.get(sensor.id);
        if (queryReading) {
          lastReading = queryReading;
          console.log(`[Sensors] ✅ Using query reading for ${sensor.name}:`, lastReading);
        } else {
          console.log(`[Sensors] ⚠️ No lastReading for sensor ${sensor.name} (${sensor.id})`);
        }
      }
      
      if (lastReading) {
        sensor.lastReading = lastReading;
      }
      
      return sensor;
    });
    
    const sensorsWithReadingsCount = sensorsWithReadings.filter(s => s.lastReading).length;
    console.log('[Sensors] Total sensors with readings:', sensorsWithReadingsCount, 'out of', sensorsWithReadings.length);
    return sensorsWithReadings;
  }, [apiSensors, lastReadingsMap, lastSensorReading]);
  
  // Invalidar query de lecturas cuando llegue una nueva lectura del WebSocket
  useEffect(() => {
    if (lastSensorReading) {
      // Invalidar la query para que se actualice con la nueva lectura
      requestAnimationFrame(() => {
        queryClient.invalidateQueries({ queryKey: ['sensor-last-readings'] });
      });
    }
  }, [lastSensorReading, queryClient]);

  // CAMBIO v0.2.0: Sensors pertenecen a empresas, no a jardines
  // Query para obtener empresas
  const { data: companiesData } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await companiesService.getAll({ pageSize: 100 });
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al cargar empresas');
      }
      return response.data;
    },
  });

  // Query para obtener servidores MQTT y LNS
  // ACTUALIZADO: Backend devuelve formato paginado { items: [], pagination: {} }
  const { data: mqttServersData } = useQuery({
    queryKey: ['mqtt-servers'],
    queryFn: async () => {
      const response = await serversService.getMQTTServers();
      if (!response.success) {
        return [];
      }
      return response.data?.items || [];
    },
  });

  const { data: lnsServersData } = useQuery({
    queryKey: ['lns-servers'],
    queryFn: async () => {
      const response = await serversService.getLNSServers();
      if (!response.success) {
        return [];
      }
      return response.data?.items || [];
    },
  });

  // Mutation para crear
  const createMutation = useMutation({
    mutationFn: (data: CreateSensorRequest) => sensorsService.create(data),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['sensors'] });
        toast({ title: 'Sensor creado', description: 'El sensor ha sido creado correctamente.' });
        setFormDialogOpen(false);
      } else {
        toast({ title: 'Error', description: response.error?.message, variant: 'destructive' });
      }
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Mutation para actualizar
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSensorRequest }) => 
      sensorsService.update(id, data),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['sensors'] });
        toast({ title: 'Sensor actualizado', description: 'El sensor ha sido actualizado correctamente.' });
        setFormDialogOpen(false);
      } else {
        toast({ title: 'Error', description: response.error?.message, variant: 'destructive' });
      }
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Mutation para eliminar
  const deleteMutation = useMutation({
    mutationFn: (id: string) => sensorsService.delete(id),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['sensors'] });
        toast({ title: 'Sensor eliminado', description: 'El sensor ha sido eliminado.', variant: 'destructive' });
        setDeleteDialogOpen(false);
        setSelectedSensor(null);
      } else {
        toast({ title: 'Error', description: response.error?.message, variant: 'destructive' });
      }
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const companies = companiesData?.items || [];

  // Filter sensors by search term
  const filteredSensors = sensors.filter(sensor =>
    sensor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sensor.deviceEUI.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // CAMBIO v0.2.0: Sensors pertenecen a empresas
  const getCompanyName = (companyId: string | undefined | null) => {
    if (!companyId) return 'Sin asignar';
    return companies.find(c => c.id === companyId)?.name || 'Sin asignar';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'bg-success';
      case 'offline': return 'bg-destructive';
      case 'warning': return 'bg-warning';
      default: return 'bg-muted';
    }
  };

  const getBatteryColor = (battery?: number) => {
    if (!battery) return 'text-muted-foreground';
    if (battery > 50) return 'text-success';
    if (battery > 20) return 'text-warning';
    return 'text-destructive';
  };

  const getHumidityColor = (humidity?: number) => {
    if (!humidity) return 'text-muted-foreground';
    if (humidity >= 40 && humidity <= 60) return 'text-success';
    if (humidity < 30 || humidity > 70) return 'text-warning';
    return 'text-foreground';
  };

  const formatLastSeen = (date?: Date) => {
    if (!date) return 'Nunca';
    const diff = Date.now() - date.getTime();
    if (diff < 60000) return 'Ahora';
    if (diff < 3600000) return `Hace ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `Hace ${Math.floor(diff / 3600000)} h`;
    return `Hace ${Math.floor(diff / 86400000)} días`;
  };

  // Handlers
  const handleViewDetails = (sensor: Sensor) => {
    setSelectedSensor(sensor);
    setDetailDialogOpen(true);
  };

  const handleViewHistory = (sensor: Sensor) => {
    setSelectedSensor(sensor);
    setHistoryDialogOpen(true);
  };

  const handleCreate = () => {
    setSelectedSensor(null);
    setFormDialogOpen(true);
  };

  const handleEdit = (sensor: Sensor) => {
    setSelectedSensor(sensor);
    setFormDialogOpen(true);
  };

  const handleDelete = (sensor: Sensor) => {
    setSelectedSensor(sensor);
    setDeleteDialogOpen(true);
  };

  // CAMBIO v0.2.0: Usar companyId y formato correcto según ENDPOINT_TESTS.md
  // IMPORTANTE: Para sensores usar lnsServerId y mqttServerId directamente, NO objetos anidados
  const handleSaveSensor = (sensor: Sensor) => {
    const isUpdating = !!selectedSensor;
    const previousType = selectedSensor?.communicationType;
    const typeChanged = isUpdating && previousType !== sensor.communicationType;
    
    const sensorData: CreateSensorRequest | UpdateSensorRequest = {
      name: sensor.name,
      model: sensor.model,
      deviceEUI: sensor.deviceEUI,
      companyId: sensor.companyId || undefined,
      lat: sensor.coordinates?.lat,
      lng: sensor.coordinates?.lng,
      communicationType: sensor.communicationType,
    };

    // Configuración según tipo de comunicación
    if (sensor.communicationType === 'mqtt') {
      // Para MQTT: mqttServerId y mqttTopic directamente
      if (sensor.mqttConfig?.serverId) {
        (sensorData as any).mqttServerId = sensor.mqttConfig.serverId;
      }
      if (sensor.mqttConfig?.topic) {
        (sensorData as any).mqttTopic = sensor.mqttConfig.topic;
      }
      
      // Si se cambió de ChirpStack a MQTT, limpiar campos LNS
      if (typeChanged && previousType === 'chirpstack') {
        (sensorData as any).lnsServerId = null;
        (sensorData as any).appKey = null;
      }
    } else {
      // Para ChirpStack: lnsServerId directamente
      if (sensor.lnsConfig?.serverId) {
        (sensorData as any).lnsServerId = sensor.lnsConfig.serverId;
      }
      if ((sensor as any).appKey) {
        (sensorData as any).appKey = (sensor as any).appKey;
      }
      
      // Si se cambió de MQTT a ChirpStack, limpiar campos MQTT
      if (typeChanged && previousType === 'mqtt') {
        (sensorData as any).mqttServerId = null;
        (sensorData as any).mqttTopic = null;
      }
    }

    if (isUpdating) {
      updateMutation.mutate({ id: selectedSensor.id, data: sensorData as UpdateSensorRequest });
    } else {
      createMutation.mutate(sensorData as CreateSensorRequest);
    }
  };

  const handleConfirmDelete = () => {
    if (selectedSensor) {
      deleteMutation.mutate(selectedSensor.id);
    }
  };

  // Stats
  const onlineCount = sensors.filter(s => s.status === 'online').length;
  const lowBatteryCount = sensors.filter(s => s.battery && s.battery < 30).length;

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-foreground">Sensores</h1>
            <p className="text-muted-foreground mt-1">Gestiona los sensores EM500-SMTC de humedad y temperatura</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => <SensorCardSkeleton key={i} />)}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-foreground">Sensores</h1>
            <p className="text-muted-foreground mt-1">Gestiona los sensores EM500-SMTC de humedad y temperatura</p>
          </div>
        </div>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>{error instanceof Error ? error.message : 'Error al cargar sensores'}</span>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Reintentar
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground">
            Sensores
          </h1>
          <p className="text-muted-foreground mt-1">
            Gestiona los sensores EM500-SMTC de humedad y temperatura
          </p>
        </div>
        {canManageDevices ? (
          <Button 
            className="gradient-primary text-white shadow-md hover:shadow-lg transition-shadow"
            onClick={handleCreate}
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Sensor
          </Button>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button disabled className="gap-2">
                <ShieldAlert className="w-4 h-4" />
                Nuevo Sensor
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Solo los superadmin pueden crear sensores</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nombre o DevEUI..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button variant="outline" className="gap-2">
          <Filter className="w-4 h-4" />
          Filtros
        </Button>
      </div>

      {/* Stats summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-success/10">
              <Wifi className="w-5 h-5 text-success" />
            </div>
            <div>
              <p className="text-2xl font-display font-bold">{onlineCount}</p>
              <p className="text-sm text-muted-foreground">Online</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-warning/10">
              <Battery className="w-5 h-5 text-warning" />
            </div>
            <div>
              <p className="text-2xl font-display font-bold">{lowBatteryCount}</p>
              <p className="text-sm text-muted-foreground">Batería baja</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-info/10">
              <Radio className="w-5 h-5 text-info" />
            </div>
            <div>
              <p className="text-2xl font-display font-bold">{sensors.length}</p>
              <p className="text-sm text-muted-foreground">Total</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Sensors grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filteredSensors.map((sensor) => (
          <motion.div key={sensor.id} variants={itemVariants}>
            <Card className="group hover:shadow-card-hover transition-all duration-300 hover:-translate-y-0.5">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      'p-2.5 rounded-xl',
                      sensor.status === 'online' ? 'bg-info/10' : 'bg-muted'
                    )}>
                      <Radio className={cn(
                        'w-5 h-5',
                        sensor.status === 'online' ? 'text-info' : 'text-muted-foreground'
                      )} />
                    </div>
                    <div>
                      <h3 className="font-display font-semibold text-foreground group-hover:text-primary transition-colors">
                        {sensor.name}
                      </h3>
                      <p className="text-xs text-muted-foreground font-mono">
                        {sensor.model}
                      </p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleViewDetails(sensor)}>
                        <Eye className="w-4 h-4 mr-2" />
                        Ver detalles
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleViewHistory(sensor)}>
                        <History className="w-4 h-4 mr-2" />
                        Histórico
                      </DropdownMenuItem>
                      {canManageDevices && (
                        <>
                          <DropdownMenuItem onClick={() => handleEdit(sensor)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            className="text-destructive focus:text-destructive"
                            onClick={() => handleDelete(sensor)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Eliminar
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Status badges */}
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge 
                    variant="outline" 
                    className={cn(
                      'gap-1.5',
                      sensor.status === 'online' && 'border-success/50 text-success',
                      sensor.status === 'offline' && 'border-destructive/50 text-destructive',
                      sensor.status === 'warning' && 'border-warning/50 text-warning'
                    )}
                  >
                    <div className={cn('w-1.5 h-1.5 rounded-full', getStatusColor(sensor.status))} />
                    {sensor.status}
                  </Badge>
                  <Badge variant="secondary">
                    {sensor.communicationType.toUpperCase()}
                  </Badge>
                </div>

                {/* Last reading */}
                {sensor.lastReading ? (
                  <div className="grid grid-cols-3 gap-2">
                    <div className="p-3 rounded-lg bg-muted/50">
                      <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                        <Thermometer className="w-3.5 h-3.5" />
                        <span className="text-xs">Temperatura</span>
                      </div>
                      <p className="text-lg font-display font-bold">
                        {sensor.lastReading.temperature}°C
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/50">
                      <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                        <Droplets className="w-3.5 h-3.5" />
                        <span className="text-xs">Humedad</span>
                      </div>
                      <p className={cn(
                        'text-lg font-display font-bold',
                        getHumidityColor(sensor.lastReading.humidity)
                      )}>
                        {sensor.lastReading.humidity}%
                      </p>
                    </div>
                    {sensor.lastReading.conductivity !== undefined && (
                      <div className="p-3 rounded-lg bg-muted/50">
                        <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                          <Zap className="w-3.5 h-3.5" />
                          <span className="text-xs">EC</span>
                        </div>
                        <p className="text-lg font-display font-bold">
                          {sensor.lastReading.conductivity} µS/cm
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-3 rounded-lg bg-muted/30 border border-dashed border-muted-foreground/30">
                    <p className="text-sm text-muted-foreground text-center">
                      Sin lecturas disponibles
                    </p>
                  </div>
                )}

                {/* Device info */}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Jardín</span>
                    <span>{getCompanyName(sensor.companyId)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Última lectura</span>
                    <span>{formatLastSeen(sensor.lastSeen)}</span>
                  </div>
                </div>

                {/* Battery */}
                <div className="pt-3 border-t border-border flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Battery className={cn('w-4 h-4', getBatteryColor(sensor.battery))} />
                    <span className={cn('text-sm font-medium', getBatteryColor(sensor.battery))}>
                      {sensor.battery ?? 0}%
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground font-mono">
                    {sensor.deviceEUI.slice(-8)}
                  </span>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {filteredSensors.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <Radio className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">
            No se encontraron sensores.
          </p>
        </motion.div>
      )}

      {/* Dialogs */}
      <SensorDetailDialog
        open={detailDialogOpen}
        onOpenChange={setDetailDialogOpen}
        sensor={selectedSensor}
        companyName={selectedSensor ? getCompanyName(selectedSensor.companyId) : undefined}
      />

      <SensorFormDialog
        open={formDialogOpen}
        onOpenChange={setFormDialogOpen}
        sensor={selectedSensor}
        companies={companies.map(c => ({ id: c.id, name: c.name }))}
        mqttServers={mqttServersData || []}
        lnsServers={lnsServersData || []}
        onSave={handleSaveSensor}
        isLoading={createMutation.isPending || updateMutation.isPending}
        userRole={currentUser?.role}
      />

      <SensorHistoryDialog
        open={historyDialogOpen}
        onOpenChange={setHistoryDialogOpen}
        sensor={selectedSensor}
      />

      <SensorDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        sensor={selectedSensor}
        onConfirm={handleConfirmDelete}
        isLoading={deleteMutation.isPending}
      />
    </motion.div>
  );
}
